"use client";

import { useFormatter } from "next-intl";
import React, { useEffect, useRef, useState } from "react";

type Props = {
  value: number;
  className?: string;
};

const DURATION_MS = 1400;

/**
 * Renders the real value on the server (SEO/no-JS), then replays a 0 -> value
 * count-up the first time the number scrolls into view.
 */
const CountUpStat: React.FC<Props> = ({ value, className }) => {
  const formatter = useFormatter();
  const ref = useRef<HTMLParagraphElement>(null);
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const node = ref.current;
    if (
      !node ||
      typeof IntersectionObserver === "undefined" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    let frame = 0;
    let started = false;

    const observer = new IntersectionObserver(
      (entries) => {
        if (started || !entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        started = true;
        observer.disconnect();
        const startAt = performance.now();

        const tick = (now: number) => {
          const progress = Math.min((now - startAt) / DURATION_MS, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setDisplay(Math.round(value * eased));

          if (progress < 1) {
            frame = requestAnimationFrame(tick);
          }
        };

        frame = requestAnimationFrame(tick);
      },
      { threshold: 0.4 },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [value]);

  return (
    <p ref={ref} className={className}>
      {formatter.number(display)}
    </p>
  );
};

export default CountUpStat;
